import { displayOf, draftDocOf, narrativeOrder, type StoryNode } from '@dispositio/core';
import { de } from '../i18n/de';
import { useFilmView } from '../state/filmView';
import { useSceneFilter } from '../state/sceneFilter';
import type { GuideEnv, GuideStep, GuideTarget } from './tutorialGuide';

/**
 * Führung durch den Demo-Roman „Schachnovelle“ (PF 47): die Fehlersuche mit dem Logik-Lektor – die Hinweise, ein Widerspruch an seiner
 * Stelle im Text (Zweigs eigener Wochentag), die roten Punkte in der Erzähllinie und der Film mit „Prüfen“. Danach die Übersichten der
 * Erzähllinie: die Auftrittsmatrix, der Filter (wo begegnen sich zwei Figuren?), die Wissensübersicht, das Strukturfenster (Story Beats)
 * und die Folgen beim Ziehen. Den Rest findet der Nutzer selbst; die Lösung steht in der Notiz „Die eingebauten Fehler“
 * (apps/server/src/services/exampleSchach.ts). Beziehungen anlegen zeigt „Verknüpfungen und Beziehungen“.
 */

const T = de.guide.schach;
const css = (selector: string): GuideTarget => ({ selector });
const present = (selector: string): boolean => typeof document !== 'undefined' && !!document.querySelector(selector);

/** die Szene mit diesem Titel (in Lesereihenfolge) */
const sceneNamed = (env: GuideEnv, title: string): StoryNode | undefined => narrativeOrder(env.ctx).scenes.find((s) => displayOf(env.ctx, s.scene).title === title)?.scene;
/** Im Manuskript ist der Szenentext dieser Szene offen */
const textOpen = (env: GuideEnv, title: string): boolean => {
  const scene = sceneNamed(env, title);
  return env.ws.view === 'editor' && !!scene && env.ws.openDoc?.nodeId === scene.id && env.ws.openDoc.docId === draftDocOf(scene)?.id;
};

// bedienbare Bereiche der Schritte (siehe `allow`)
const ISSUES_BTN = '[data-testid="open-issues"]';
const WEEKDAY = '[data-testid="issue"][data-code="time-weekday"]';
const PLANNING = '[data-testid="view-planning"]';
const BAND = '[data-testid="narrative-band"]';
const FILM_BTN = '[data-testid="line-film"]';
const FILM = '[data-testid="film"]';
const MATRIX_BTN = '[data-testid="line-matrix"]';
const MATRIX = '[data-testid="matrix-panel"]';
const FILTER_BTN = '[data-testid="line-filter"]';
const FILTER = '[data-testid="filter-panel"]';
const KNOW_BTN = '[data-testid="line-knowledge"]';
const KNOW = '[data-testid="knowledge-panel"]';
const STRUCTURE_BTN = '[data-testid="line-structure"]';
const STRUCTURE = '[data-testid="structure-panel"]';
// das ✕ der Fenster, die der Nutzer schließen soll: ein eigener Rahmen, den die Textbox nicht verdeckt
const MATRIX_CLOSE = '[data-testid="matrix-panel-close"]';
const FILTER_CLOSE = '[data-testid="filter-panel-close"]';
const KNOW_CLOSE = '[data-testid="knowledge-panel-close"]';
const STRUCTURE_CLOSE = '[data-testid="structure-panel-close"]';
/** die Seite des Manuskripts (nicht die Textfelder der Seitenleiste, die auch .rt-content sind) */
const PAGE = '.rt-content.rt-page';

export const SCHACH_STEPS: GuideStep[] = [
  { id: 'schach-start', text: () => T.start, targets: () => [css(BAND)] },
  { id: 'schach-hints', text: () => T.hints, targets: () => [css(ISSUES_BTN)], until: () => present('[data-testid="issues-list"]'), allow: [ISSUES_BTN], blink: true },
  {
    id: 'schach-list',
    text: () => T.list,
    targets: () => [css(WEEKDAY)],
    also: () => [[css('[data-testid="issues-list"]')]],
    until: (e) => textOpen(e, 'Der Mantel'),
    allow: [`${WEEKDAY} [data-testid="issue-show"]`],
  },
  // die Stelle im Text: anklicken zeigt die Auswege (der Text bleibt bearbeitbar, sonst nichts)
  { id: 'schach-weekday', text: () => T.weekday, targets: () => [css(`${PAGE} .rt-conflict`), css('[data-testid="doc-rich"]')], dock: 'side', allow: [PAGE] },
  { id: 'schach-back', text: () => T.back, targets: () => [css(PLANNING)], until: (e) => e.ws.view === 'planning', allow: [PLANNING] },
  { id: 'schach-dots', text: () => T.dots, targets: () => [css(`${BAND} [data-testid="issue-dot"]`), css(BAND)] },
  // der Film; nach dem Neuladen ist er zu: sein Knopf bleibt bedienbar
  { id: 'schach-film', text: () => T.film, targets: () => [css(FILM_BTN)], until: () => present(FILM), allow: [FILM_BTN] },
  { id: 'schach-check', text: () => T.check, targets: () => [css('[data-testid="film-check"]'), css(FILM_BTN)], until: () => present(FILM) && useFilmView.getState().check, allow: [FILM, FILM_BTN] },
  { id: 'schach-filmClose', text: () => T.filmClose, targets: () => [css('[data-testid="film-close"]'), css(FILM)], until: () => !present(FILM), allow: [FILM] },
  // Auftrittsmatrix: öffnen, lesen
  { id: 'schach-matrix', text: () => T.matrix, targets: () => [css(MATRIX_BTN)], until: () => present(MATRIX), allow: [MATRIX_BTN] },
  { id: 'schach-matrixRead', text: () => T.matrixRead, targets: () => [css(MATRIX)] },
  // Filter: wo begegnen sich Czentovic und Dr. B.? Danach aufheben und schließen
  { id: 'schach-filter', text: () => T.filter, targets: () => [css(FILTER_BTN)], also: () => [[css(MATRIX_CLOSE)]], until: () => useSceneFilter.getState().open, allow: [FILTER_BTN, MATRIX] },
  { id: 'schach-filterPick', text: () => T.filterPick, targets: () => [css(FILTER), css(FILTER_BTN)], until: () => useSceneFilter.getState().entityIds.length >= 2, allow: [FILTER, FILTER_BTN] },
  {
    id: 'schach-filterDone',
    text: () => T.filterDone,
    targets: () => [css(BAND)],
    also: () => [[css(FILTER)], [css(FILTER_CLOSE)]],
    until: () => {
      const f = useSceneFilter.getState();
      return !f.open && f.entityIds.length === 0;
    },
    allow: [FILTER, FILTER_BTN],
  },
  // Wissensübersicht: öffnen, lesen, schließen
  { id: 'schach-knowledge', text: () => T.knowledge, targets: () => [css(KNOW_BTN)], until: () => present(KNOW), allow: [KNOW_BTN] },
  { id: 'schach-knowledgeRead', text: () => T.knowledgeRead, targets: () => [css(KNOW)], also: () => [[css(KNOW_CLOSE)]], until: () => !present(KNOW), allow: [KNOW] },
  // Strukturfenster (Story Beats): öffnen, lesen, schließen
  { id: 'schach-structure', text: () => T.structure, targets: () => [css(STRUCTURE_BTN)], until: () => present(STRUCTURE), allow: [STRUCTURE_BTN] },
  { id: 'schach-structureRead', text: () => T.structureRead, targets: () => [css(STRUCTURE)], also: () => [[css(STRUCTURE_CLOSE)]], until: () => !present(STRUCTURE), allow: [STRUCTURE] },
  // Folgen beim Ziehen und das Übersichtsfenster: nur erklärt
  { id: 'schach-drag', text: () => T.drag, targets: () => [css('[data-testid="graph-canvas"]')] },
  { id: 'schach-end', text: () => T.end, free: true },
];
